/**
 * L'HABILLAGE D'UNE TUILE AU STUDIO — sa teinte, son visuel, son picto, sa photo et son cadrage.
 *
 * Transposé de la maquette (inspecteur « Visuel de la tuile », « Couleur », « Pictogramme », fenêtre « Cadrer la
 * photo »). Ce que la tuile MONTRE se décide au contrat (`tileFace`) : le composeur, l'aperçu et la caisse appliquent
 * la même règle, et seul ce qu'on leur donne à montrer change.
 *
 * ⚠️ LES TEINTES SONT DES JETONS DE LA CHARTE (`QUICK_TILE_PALETTE`) : la tuile suit le thème clair ou sombre.
 * ⚠️ LA PHOTO PART AU STOCKAGE DU BACK-OFFICE AVANT D'ENTRER AU BROUILLON : le document ne porte qu'une adresse, et la
 *    photo est préparée par le navigateur (1600 px, WebP, vignette) comme celles des fiches article.
 * ⚠️ LA CAISSE TÉLÉCHARGE LA PHOTO (vignette) par la porte des appareils du serveur de sync, et la cadre au même pixel
 *    (`photoFrame`, au contrat) : le cadrage choisi ici est celui qu'elle montre.
 */
import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { Button, Icon, Modal, Trans, rovingRadio, type IconName } from '@orgazone/ui-web';
import {
  QUICK_RAIL, QUICK_RAIL_POCKET, QUICK_TILE, QUICK_TILE_BODY, QUICK_TILE_ICONS, QUICK_TILE_PALETTE, QUICK_VEIL, isIconName, quickBandBox, quickRailInitials, quickRailLook,
  quickTileHeight, webPaperVeil,
} from '@orgazone/ui-tokens';
import {
  QUICK_PHOTO_FOCAL_MAX, QUICK_PHOTO_ZOOM_MAX, QUICK_PHOTO_ZOOM_MIN, QUICK_TILE_COLORS, QUICK_TILE_DEFAULT_ICON,
  NO_SCREEN_VISUALS, screenLook, tileFace, type QuickPhotoSize, type QuickScreenVisuals, type QuickTileColor, type QuickTileFace, type QuickTileKind, type QuickTileLook, type QuickTileVisual,
} from '@orgazone/sync-contract';
import { api, type StudioArticle, type StudioTile, type StudioTilePhoto } from '../api';
import { key, t, type Translatable } from '../i18n';

/**
 * Le nom de chaque teinte de tuile, pour qui ne voit pas la pastille.
 * ⚠️ FERMÉ PAR LE TYPE : une teinte ajoutée au contrat réclame son nom ici ; sans lui, la pastille s'annonçait par sa
 *    clé technique (« teal »), la même dans toutes les langues.
 */
export const TILE_COLOR_NAME: Record<QuickTileColor, Translatable> = {
  teal: key('Sarcelle'),
  amber: key('Ambre'),
  rose: key('Rose'),
  violet: key('Violet'),
  blue: key('Bleu'),
  green: key('Vert'),
  orange: key('Orange'),
  ink: key('Encre'),
};
import { motifNonPreparable, preparerPhoto } from './photo-preparation';
import type { TileLook, TilePatch } from './quick-studio-model';

/** Le mur du serveur — la préparation vise bien plus bas, ce plafond ne sert qu'au repli (`photo-store.ts`). */
const SERVER_MAX_BYTES = 1024 * 1024;
const CENTER = QUICK_PHOTO_FOCAL_MAX / 2;
/** Une image sans cadrage choisi : centrée, sans zoom — la photo du comptoir et le visuel de fiche. */
export const NO_FRAMING = { x: CENTER, y: CENTER, zoom: QUICK_PHOTO_ZOOM_MIN } as const;

/** Les couleurs d'une teinte, en variables CSS — `null` = la tuile garde les couleurs d'avant l'habillage. */
export interface TileTone { bg: string; fg: string; bd: string; dot: string }
export function tileTone(color: QuickTileColor | null): TileTone | null {
  if (color === null) return null;
  const p = QUICK_TILE_PALETTE[color];
  return { bg: `var(--${p.bg})`, fg: `var(--${p.fg})`, bd: `var(--${p.bd})`, dot: `var(--${p.dot})` };
}

/** Un picto que la charte sait dessiner — un nom inconnu (serveur plus récent, icône retirée) prend le picto par défaut. */
export function knownIcon(name: string): IconName {
  return isIconName(name) ? name : (QUICK_TILE_DEFAULT_ICON as IconName);
}

/** L'image d'une photo cadrée : position du point focal et zoom, depuis ce même point. */
export function photoStyle(photo: Pick<StudioTilePhoto, 'x' | 'y' | 'zoom'>, uri: string): CSSProperties {
  const at = `${photo.x / 100}% ${photo.y / 100}%`;
  return {
    display: 'block', width: '100%', height: '100%',
    // ⚠️ Une chaîne CSS échappée, pas une adresse collée entre guillemets : un guillemet dans l'adresse fermerait la valeur.
    backgroundImage: `url(${JSON.stringify(uri)})`, backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat', backgroundPosition: at, transform: `scale(${photo.zoom / 100})`, transformOrigin: at,
  };
}

/** Le cadrage d'une photo : point focal et zoom, en centièmes (contrat). */
export type TileFraming = Pick<StudioTilePhoto, 'x' | 'y' | 'zoom'>;

/**
 * CE QU'UNE TUILE MONTRE AU STUDIO, SA TEINTE ET SON CADRAGE — la règle de la caisse (`tileFace`, au contrat) appliquée
 * aux photos que le studio connaît : la photo du comptoir et le visuel de fiche de l'article, la photo posée au studio.
 * ⚠️ LE COMPOSEUR ET L'APERÇU LA LISENT TOUS DEUX : écrite à chaque rendu, l'un montrerait une photo que l'autre tait.
 * ⚠️ SEULE LA PHOTO DU STUDIO PORTE UN CADRAGE : celles du comptoir et de la fiche se montrent centrées.
 * ⚠️ LE VISUEL : le sien, sinon celui de tous les articles de l'écran (un article), sinon celui que sa source donne à ses
 *    articles, sinon celui de l'écran (`screenLook`, au contrat) — la caisse applique la même règle.
 * ⚠️ `missingPhoto` : la taille de la photo qu'on a CHOISIE et qui n'existe pas (`null` sinon). La caisse montre alors le
 *    picto, et c'est juste ; le composeur garde la place de la photo et dit de l'ajouter — sans quoi « Photo » choisi sur
 *    un dossier sans photo semblait n'avoir rien fait.
 * - `tile` absente : un article DÉVERSÉ ; `fromSource` : l'habillage des articles de la source du niveau (`null` ailleurs) ;
 * - `screen` : les visuels que la disposition donne à tout l'écran et à tous ses articles.
 */
export function studioFace(
  tile: Pick<StudioTile, 'kind' | 'visual' | 'icon' | 'photo' | 'photoSize' | 'color'> | undefined,
  article?: Pick<StudioArticle, 'counterPhoto' | 'catalogPhoto'> | null,
  fromSource: QuickTileLook | null = null,
  screen: QuickScreenVisuals = NO_SCREEN_VISUALS,
): { face: QuickTileFace; framing: TileFraming; color: QuickTileColor | null; missingPhoto: QuickPhotoSize | null } {
  const photo = tile?.photo ?? null;
  const kind = tile?.kind ?? 'article';
  const own: QuickTileLook | null = tile ? { color: tile.color, visual: tile.visual, icon: tile.icon, photoSize: tile.photoSize } : null;
  const look = screenLook(kind, own, kind === 'article' ? fromSource : null, screen);
  const face = tileFace({ kind, ...look }, {
    counter: article?.counterPhoto ?? null,
    catalog: article?.catalogPhoto ?? null,
    studio: photo ? photo.thumbUrl ?? photo.url : null,
  });
  const missingPhoto = look.visual === 'photo' && face.kind !== 'photo' ? look.photoSize ?? 'normal' : null;
  return { face, framing: face.kind === 'photo' && face.from === 'studio' && photo ? photo : NO_FRAMING, color: look.color, missingPhoto };
}

/** La photo qu'une tuile MONTRE, et d'où elle vient — `null` quand elle n'en montre aucune. */
export type ShownPhoto = { uri: string; from: 'counter' | 'studio' | 'catalog' } | null;
export function shownPhoto(face: QuickTileFace): ShownPhoto {
  return face.kind === 'photo' ? { uri: face.uri, from: face.from } : null;
}

/** Les couleurs du studio que la règle du rail emploie hors teinte : le papier, la sélection, le neutre. */
const RAIL_BASE = { paper: 'var(--paper)', selected: 'var(--teal-100)', neutral: 'var(--ink-100)', neutralFg: 'var(--ink-600)', veil: webPaperVeil(QUICK_VEIL.rail) } as const;

/**
 * UN BOUTON DU RAIL « Menu fixe » — sa teinte, son visuel de 26 px (picto ou photo, par la règle de la caisse) et son
 * nom, aux mesures de la caisse (`QUICK_RAIL`, `quickRailLook`).
 * ⚠️ LE COMPOSEUR ET L'APERÇU LE PARTAGENT : écrit deux fois, l'un des deux rails divergerait de la caisse.
 * ⚠️ LE CADRE EST EN FEUILLE (`oz-quick-rail-btn`) : posé en ligne, il primerait sur le survol de la maquette.
 * - `open` : le niveau que la grille montre (cadre de la marque, nom en gras) ;
 * - `active` : le bouton teinté — au composeur, aussi la tuile que l'inspecteur règle ;
 * - `edit` : AU COMPOSEUR SEULEMENT — la poignée qui le déplace dans le menu (même geste que les tuiles) et la croix qui
 *   le retire ; la caisse et l'aperçu n'en ont pas.
 */
export function RailButton({ tile, name, article, open, active, onClick, edit, screen }: {
  tile: StudioTile; name: string; article?: Pick<StudioArticle, 'counterPhoto' | 'catalogPhoto'> | null;
  open: boolean; active: boolean; onClick: () => void; edit?: RailEdit;
  /** Les visuels de la disposition : un bouton sans visuel à lui prend celui de l'écran, comme une tuile. */
  screen?: QuickScreenVisuals;
}) {
  const look = quickRailLook(tileTone(tile.color), active, RAIL_BASE);
  const { face, framing } = studioFace(tile, article, null, screen);
  const box: CSSProperties = { flex: `0 0 ${QUICK_RAIL.visual}px`, width: QUICK_RAIL.visual, height: QUICK_RAIL.visual, borderRadius: 'var(--r-xs)', overflow: 'hidden' };
  const className = open ? 'oz-quick-rail-btn oz-quick-rail-btn--open' : 'oz-quick-rail-btn';
  const content = (
    <>
      {face.kind === 'photo' && <span aria-hidden style={box}><span style={photoStyle(framing, face.uri)} /></span>}
      {face.kind === 'icon' && (
        <span aria-hidden style={{ ...box, background: look.iconBox, color: look.iconColor, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name={knownIcon(face.icon)} size={QUICK_RAIL.glyph} stroke={QUICK_TILE_BODY.glyphStroke} />
        </span>
      )}
      {/* ⚠️ LE NOM VA À LA LIGNE AUTANT QU'IL LE FAUT, ENTRE SES MOTS (maquette) : borné à deux lignes, « Sandwichs &
          salades » perdait son dernier mot et « Boissons » se coupait en plein mot. */}
      <span style={{ flex: 1, minWidth: 0, fontSize: QUICK_RAIL.font, fontWeight: open ? 700 : 600, color: 'var(--ink-900)', lineHeight: QUICK_RAIL.lineHeight }}>
        {name}
      </span>
    </>
  );
  if (!edit) {
    return (
      <button type="button" onClick={onClick} className={className}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: QUICK_RAIL.buttonMinHeight, padding: QUICK_RAIL.buttonPad, flexShrink: 0, textAlign: 'left', borderRadius: 'var(--r-sm)', background: look.background }}>
        {content}
      </button>
    );
  }
  // ⚠️ UN GROUPE, PAS UN BOUTON : le bouton du menu porte sa poignée et sa croix, qu'un `<button>` ne peut pas contenir.
  //    Le choisir passe par le bouton qui porte son visuel et son nom ; la poignée seule saisit (maquette).
  return (
    // ⚠️ LE CLAVIER RANGE AUSSI : ↑/↓ sur le bouton le déplacent d'un cran dans le menu — le même geste que la poignée,
    //    que le pointeur seul atteint (elle reste cachée aux aides techniques).
    <div ref={edit.tile.ref} data-tile-key={edit.tile['data-tile-key']} role="group" aria-label={name} className={className}
      onKeyDown={(e) => {
        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
        e.preventDefault();
        edit.onMove(e.key === 'ArrowUp' ? -1 : 1);
      }}
      style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', minHeight: QUICK_RAIL.buttonMinHeight, padding: `${QUICK_RAIL.buttonPad}px ${QUICK_RAIL.buttonPad}px ${QUICK_RAIL.buttonPad}px 4px`, flexShrink: 0, boxSizing: 'border-box', borderRadius: 'var(--r-sm)', background: look.background, ...edit.tile.style }}>
      <span aria-hidden data-qs-rail-grip className="oz-qs-rail-grip" onPointerDown={edit.grip.onPointerDown}
        style={{ width: 14, height: 26, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, borderRadius: 4, ...edit.grip.style }}>
        <Icon name="drag" size={12} />
      </span>
      {/* ⚠️ 6 PX ENTRE LE PICTO ET LE NOM, comme entre la poignée et le picto : au composeur, la maquette pose les quatre
          éléments côte à côte au même écart (l'aperçu, sans poignée, en garde 8). */}
      <button type="button" onClick={onClick} disabled={edit.disabled}
        style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6, padding: 0, border: 'none', background: 'transparent', textAlign: 'left' }}>
        {content}
      </button>
      <button type="button" className="oz-qs-rail-del" disabled={edit.disabled} onClick={edit.onRemove}
        aria-label={t('Retirer « {nom} » du menu', { nom: name })} title={t('Retirer du menu')}
        style={{ width: 20, height: 20, flexShrink: 0, border: 'none', borderRadius: 'var(--r-xs)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
        <Icon name="x" size={11} stroke={2.6} />
      </button>
    </div>
  );
}

/**
 * UN BOUTON DU MENU FIXE DU TÉLÉPHONE — son visuel seul, sur 44 px ; le nom dans l'infobulle et le libellé d'accessibilité
 * (maquette `pRailPocket`). Les couleurs et le visuel suivent la règle du bouton entier (`quickRailLook`, `studioFace`).
 * ⚠️ UN BOUTON SANS PICTO NI PHOTO GARDE UNE MARQUE LISIBLE, SES INITIALES : réduit à son visuel, un « Texte seul » serait
 *    une case vide que rien ne distingue de sa voisine.
 */
export function RailPictoButton({ tile, name, article, open, onClick, screen }: {
  tile: StudioTile; name: string; article?: Pick<StudioArticle, 'counterPhoto' | 'catalogPhoto'> | null;
  open: boolean; onClick: () => void; screen?: QuickScreenVisuals;
}) {
  const look = quickRailLook(tileTone(tile.color), open, RAIL_BASE);
  const { face, framing } = studioFace(tile, article, null, screen);
  const box: CSSProperties = {
    width: QUICK_RAIL_POCKET.visual, height: QUICK_RAIL_POCKET.visual, borderRadius: 'var(--r-xs)', overflow: 'hidden',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: look.iconBox, color: look.iconColor,
  };
  return (
    <button type="button" onClick={onClick} aria-label={name} title={name}
      className={open ? 'oz-quick-rail-btn oz-quick-rail-btn--open' : 'oz-quick-rail-btn'}
      style={{ width: QUICK_RAIL_POCKET.button, height: QUICK_RAIL_POCKET.button, flexShrink: 0, padding: 0, boxSizing: 'border-box', borderRadius: 'var(--r-sm)', background: look.background, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      {face.kind === 'photo' && <span aria-hidden style={box}><span style={photoStyle(framing, face.uri)} /></span>}
      {face.kind === 'icon' && <span aria-hidden style={box}><Icon name={knownIcon(face.icon)} size={QUICK_RAIL_POCKET.glyph} stroke={QUICK_TILE_BODY.glyphStroke} /></span>}
      {face.kind !== 'photo' && face.kind !== 'icon' && (
        <span aria-hidden style={{ ...box, fontSize: QUICK_RAIL_POCKET.initials, fontWeight: 800, letterSpacing: '.02em' }}>{quickRailInitials(name)}</span>
      )}
    </button>
  );
}

/** Ce que le composeur pose sur un bouton du menu fixe : sa mesure pour le glisser, sa poignée, sa croix. */
export interface RailEdit {
  tile: { ref: (el: HTMLElement | null) => void; style: CSSProperties; 'data-tile-key': string };
  grip: { onPointerDown: (e: ReactPointerEvent) => void; style: CSSProperties };
  onRemove: () => void;
  /** Déplace le bouton d'un cran dans le menu (−1 vers le haut, +1 vers le bas) — le geste du clavier. */
  onMove: (delta: -1 | 1) => void;
  disabled: boolean;
}

/** Le rail lui-même : ses marges, l'écart entre ses boutons, et son défilement quand ils sont nombreux. */
export function railStyle(maxHeight: number | null): CSSProperties {
  return {
    flex: `0 0 ${QUICK_RAIL.width}px`, minWidth: 0, borderRight: '1px solid var(--ink-200)', background: 'var(--ink-50)',
    padding: `${QUICK_RAIL.padY}px ${QUICK_RAIL.padX}px`, display: 'flex', flexDirection: 'column', gap: QUICK_RAIL.gap,
    overflowY: 'auto', ...(maxHeight === null ? {} : { maxHeight }),
  };
}

/**
 * Les quatre visuels, dans l'ordre et sous les mots de la maquette — et la taille de leur picto dans l'inspecteur
 * (`size`) et dans le menu « Nouvelles tuiles » (`menuSize`) : le grand picto s'y dessine plus grand que le petit.
 */
export const VISUALS: { key: QuickTileVisual; label: () => string; icon: IconName; size: number; menuSize: number }[] = [
  { key: 'none', label: () => t('Texte seul'), icon: 'letterT', size: 16, menuSize: 15 },
  { key: 'icon', label: () => t('Petit picto'), icon: 'tag', size: 15, menuSize: 13 },
  { key: 'large_icon', label: () => t('Grand picto'), icon: 'tag', size: 22, menuSize: 17 },
  { key: 'photo', label: () => t('Photo'), icon: 'image', size: 18, menuSize: 15 },
];

/** Les tailles d'une photo, sous leurs mots et leur hauteur en caisse (charte, colonne de référence). */
const SIZES: { key: QuickPhotoSize; label: () => string }[] = [
  { key: 'normal', label: () => t('Grande') },
  { key: 'reduced', label: () => t('Réduite') },
  { key: 'left', label: () => t('À gauche') },
];

/** La version qu'aura la tuile avec cette taille de photo — d'où son dessin et son cadre (`quickTileVariant`). */
function sizedVariant(kind: QuickTileKind, size: QuickPhotoSize): 'photo' | 'photoReduced' | 'photoLeft' | 'vignette' | 'vignetteReduced' | 'thumb' {
  if (kind !== 'article') return size === 'left' ? 'photoLeft' : size === 'reduced' ? 'photoReduced' : 'photo';
  return size === 'left' ? 'thumb' : size === 'reduced' ? 'vignetteReduced' : 'vignette';
}

/**
 * LES RÉGLAGES D'HABILLAGE DE L'INSPECTEUR — visuel, taille de la photo, photo, teinte, picto.
 * ⚠️ CHAQUE GESTE REND UN CORRECTIF (`onChange`) : sur un article déversé, il le retouche en tuile à part entière — le
 *    conteneur décide, ce bloc ne sait pas d'où vient la tuile.
 * - `frame` : l'encombrement de la tuile — la photo se cadre au bandeau (ou au caisson) qu'il donnera en caisse ;
 * - `forArticles` : l'habillage que la tuile d'une source donne à SES ARTICLES — pas de photo à déposer (chaque
 *   article montre la sienne), ni de cadrage ;
 * - `inherited` : un article dans le niveau d'une source — ce qu'il ne règle pas vient de sa source, et l'écran le dit ;
 * - `shown` : la photo que la tuile montre vraiment (celle du comptoir, du studio ou de la fiche) — sa vignette et sa
 *   provenance ;
 * - `photoAsk` : un compteur que le composeur fait monter au second clic sur la zone photo de la tuile — cadrer sa photo
 *   du studio si elle en a une, sinon en faire choisir une.
 */
export function TileLookEditor({ look, kind, name, frame, disabled, onChange, forArticles = false, inherited = false, shown = null, photoAsk = 0 }: {
  look: TileLook; kind: QuickTileKind; name: string; frame: { w: 1 | 2; h: 1 | 2 }; disabled: boolean;
  onChange: (patch: TilePatch, toast?: string) => void; forArticles?: boolean; inherited?: boolean;
  shown?: ShownPhoto; photoAsk?: number;
}) {
  /** Le préfixe des identifiants qui relient chaque champ au texte qui le présente. */
  const fieldId = useId();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [crop, setCrop] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const tone = tileTone(look.color);
  const visual = look.visual;
  // ⚠️ LE PICTO SE CHOISIT SUR TOUT VISUEL SAUF LA PHOTO (maquette) : un texte seul le garde pour le jour où il passe en
  //    picto, et une photo absente se dessine avec lui.
  const showIcons = visual !== 'photo';
  // ⚠️ UN ARTICLE SANS VISUEL CHOISI MONTRE AUSSI UNE PHOTO (la vignette de la vente) : sa taille se règle de même.
  const showSizes = visual === 'photo' || (kind === 'article' && visual === null);
  const size = look.photoSize ?? 'normal';
  const caption2 = forArticles ? t('Visuel de ses articles') : t('Visuel de la tuile');

  /** Prépare la photo choisie, la dépose au stockage, et l'attache à la tuile — cadrée au centre, puis à cadrer. */
  const upload = async (file: File): Promise<void> => {
    setFailure(null);
    setBusy(true);
    try {
      const blocking = motifNonPreparable(file);
      if (blocking) throw new Error(blocking);
      const ready = await preparerPhoto(file, SERVER_MAX_BYTES);
      const stored = await api.uploadQuickStudioPhoto(ready.fichier, ready.vignette);
      onChange({ visual: 'photo', photo: { url: stored.url, thumbUrl: stored.thumbUrl, x: CENTER, y: CENTER, zoom: QUICK_PHOTO_ZOOM_MIN } },
        t('Photo déposée — cadrez-la au format de la tuile'));
      setCrop(true);
    } catch (e) {
      // ⚠️ LE MOTIF RÉEL : « format non reconnu » et « stockage non configuré » appellent deux gestes différents.
      setFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Le second clic sur la zone photo, au composeur : cadrer la photo du studio, sinon en faire choisir une.
  // ⚠️ SEULE LA PHOTO DU STUDIO SE CADRE : celles du comptoir et de la fiche se montrent centrées.
  const asked = useRef(photoAsk);
  useEffect(() => {
    if (photoAsk === asked.current) return;
    asked.current = photoAsk;
    if (forArticles || disabled) return;
    if (look.photo) setCrop(true); else fileRef.current?.click();
  }, [photoAsk, forArticles, disabled, look.photo]);

  // La liste FIXE de la charte ; le picto que la tuile porte déjà, s'il n'y est pas, passe devant pour rester visible.
  const current = look.icon ?? QUICK_TILE_DEFAULT_ICON;
  const listed = (QUICK_TILE_ICONS as readonly string[]).includes(current);
  const icons: IconName[] = [...(!listed && isIconName(current) ? [current] : []), ...QUICK_TILE_ICONS];
  return (
    <>
      <div>
        <span style={caption}>{caption2}</span>
        <div role="radiogroup" aria-label={caption2} style={{ display: 'flex', gap: 6 }}>
          {VISUALS.map((v, i) => {
            const on = visual === v.key;
            return (
              <button key={v.key} type="button" role="radio" aria-checked={on} disabled={disabled}
                {...rovingRadio({ index: i, count: VISUALS.length, checked: on, anyChecked: VISUALS.some((x) => x.key === visual), onPick: (k) => onChange({ visual: VISUALS[k]!.key }) })}
                onClick={() => onChange({ visual: v.key })}
                style={{ flex: 1, minWidth: 0, height: 60, border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-ctrl)', background: on ? 'var(--teal-100)' : 'var(--paper)', color: on ? 'var(--teal-800)' : 'var(--ink-600)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '0 4px' }}>
                <Icon name={v.icon} size={v.size} />
                <span style={{ fontSize: 'var(--t-sub)', fontWeight: 600, whiteSpace: 'nowrap' }}>{v.label()}</span>
              </button>
            );
          })}
        </div>
        {/* Le visuel coché est celui que la caisse dessinera (maquette `visOf`) ; seul « Ses articles » peut n'en porter aucun. */}
        {visual === null && forArticles && <span style={hint}>{t('Aucun visuel choisi : ses articles prennent celui de l’écran.')}</span>}
        {inherited && <span style={hint}>{t('Ce qui n’est pas réglé ici vient de sa famille, de sa sous-famille ou de sa marque (« Ses articles »).')}</span>}
      </div>

      {showSizes && (
        <div>
          <span style={caption}>{t('Taille de la photo')}</span>
          <div role="radiogroup" aria-label={t('Taille de la photo')} style={{ display: 'flex', gap: 6 }}>
            {SIZES.map((s, i) => {
              const on = size === s.key;
              const v = sizedVariant(kind, s.key);
              return (
                <button key={s.key} type="button" role="radio" aria-checked={on} disabled={disabled}
                  {...rovingRadio({ index: i, count: SIZES.length, checked: on, anyChecked: SIZES.some((x) => x.key === size), onPick: (k) => onChange({ photoSize: SIZES[k]!.key }) })}
                  onClick={() => onChange({ photoSize: s.key })}
                  style={{ flex: 1, minWidth: 0, height: 64, border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-ctrl)', background: on ? 'var(--teal-100)' : 'var(--paper)', color: on ? 'var(--teal-800)' : 'var(--ink-600)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '0 4px' }}>
                  <SizeGlyph variant={v} />
                  <span style={{ fontSize: 'var(--t-meta)', fontWeight: 600, whiteSpace: 'nowrap' }}>{s.label()}</span>
                  <span style={{ fontSize: 'var(--t-micro)', color: on ? 'var(--teal-700)' : 'var(--ink-400)', fontVariantNumeric: 'tabular-nums' }}>
                    {t('{v} px', { v: quickTileHeight(v, { unitW: QUICK_TILE.width, h: 1 }) })}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {visual === 'photo' && forArticles && (
        <span style={{ ...hint, marginTop: -6 }}>{t('Chaque article montre sa photo : celle du comptoir, sinon celle de sa fiche ; sans photo, son picto.')}</span>
      )}

      {/* ⚠️ LE CHOIX DU FICHIER VIT HORS DU BLOC PHOTO : le second clic sur la zone photo d'une tuile qui n'est pas encore en
          « Photo » (un article non habillé) doit aussi pouvoir l'ouvrir. */}
      {!forArticles && (
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden aria-label={t('Choisir la photo de la tuile')}
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void upload(f); }} />
      )}
      {visual === 'photo' && !forArticles && (
        // Le glisser-déposer reste actif sur tout le bloc, avec ou sans photo (maquette).
        <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f && !disabled && !busy) void upload(f); }}>
          {shown === null ? (
            <button type="button" className="oz-qs-photo-add" disabled={disabled || busy} onClick={() => fileRef.current?.click()}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 7, width: '100%', padding: '16px 10px 14px', borderRadius: 'var(--r-ctrl)', textAlign: 'center' }}>
              <span aria-hidden style={{ width: 36, height: 36, borderRadius: 'var(--r-pill)', background: 'var(--teal-100)', color: 'var(--teal-800)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon name="upload" size={17} stroke={2} />
              </span>
              {/* ⚠️ UNE SEULE PHRASE SUR DEUX LIGNES, UNE SEULE CLÉ : coupée, chaque langue recevrait deux morceaux sans leur lien. */}
              {busy
                ? <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Envoi…')}</span>
                : <Trans k='<1>Ajouter une photo</1><2>ou glissez une image ici</2>' parts={[
                    (x) => <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)' }}>{x}</span>,
                    (x) => <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', marginTop: -3 }}>{x}</span>,
                  ]} />}
            </button>
          ) : (
            // Avec une photo : UNE rangée — la vignette (qui cadre la photo du studio), d'où vient la photo, puis remplacer
            // et retirer. Seule la photo du studio se cadre ou se retire : celles du comptoir et de la fiche ne lui
            // appartiennent pas.
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)' }}>
              {shown.from === 'studio' && look.photo ? (
                <button type="button" disabled={disabled || busy} onClick={() => setCrop(true)} aria-label={t('Cadrer la photo')} title={t('Cadrer la photo')}
                  className="oz-qs-photo-thumb" style={{ width: 44, height: 44, padding: 0, flexShrink: 0, borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', overflow: 'hidden', position: 'relative' }}>
                  <span style={photoStyle(look.photo, look.photo.thumbUrl ?? look.photo.url)} />
                </button>
              ) : (
                <span aria-hidden style={{ width: 44, height: 44, flexShrink: 0, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', overflow: 'hidden', position: 'relative', boxSizing: 'border-box' }}>
                  <span style={photoStyle(NO_FRAMING, shown.uri)} />
                </span>
              )}
              <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-900)', lineHeight: 1.35 }}>
                {shown.from === 'studio' ? t('Photo posée dans le studio') : shown.from === 'counter' ? t('Photo prise au comptoir') : t('Photo de la fiche article')}
              </span>
              <span style={{ display: 'inline-flex', gap: 5, flexShrink: 0 }}>
                <button type="button" className="oz-qs-photo-act" disabled={disabled || busy} onClick={() => fileRef.current?.click()}
                  aria-label={t('Remplacer la photo')} title={t('Remplacer la photo')}
                  style={{ width: 30, height: 30, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--r-ctrl)', padding: 0 }}>
                  <Icon name="refresh" size={14} stroke={2} />
                </button>
                {look.photo && (
                  <button type="button" className="oz-qs-photo-del" disabled={disabled || busy} onClick={() => onChange({ photo: null }, t('Photo retirée — la tuile retombe sur son picto'))}
                    aria-label={t('Retirer la photo')} title={t('Retirer la photo')}
                    style={{ width: 30, height: 30, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--r-ctrl)', padding: 0 }}>
                    <Icon name="trash" size={14} stroke={2} />
                  </button>
                )}
              </span>
            </div>
          )}
          <span style={{ ...hint, marginTop: 6 }}>
            {t('Cliquez la vignette pour cadrer. Sans photo posée ici, la tuile prend celle de la fiche article ; sans image du tout, son pictogramme.')}
          </span>
        </div>
      )}
      {!forArticles && failure && <span role="alert" style={{ ...hint, color: 'var(--rose-fg)', marginTop: -8 }}>{failure}</span>}

      <div>
        <span style={caption}>{forArticles ? t('Couleur de ses articles') : t('Couleur|teinte de la tuile')}</span>
        {/* ⚠️ « AUCUNE » N'EXISTE QUE POUR SES ARTICLES : là, elle veut dire « chaque article garde la sienne ». Une tuile, elle,
            prend une couleur de la charte (maquette) — la retirer ne laisserait rien à montrer. */}
        <div role="radiogroup" aria-label={forArticles ? t('Couleur de ses articles') : t('Couleur|teinte de la tuile')} style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          {forArticles && (
            <button type="button" role="radio" aria-checked={look.color === null} disabled={disabled} title={t('Aucune — chaque article garde la sienne')}
              {...rovingRadio({ index: 0, count: QUICK_TILE_COLORS.length + 1, checked: look.color === null, anyChecked: true, onPick: (k) => onChange({ color: k === 0 ? null : QUICK_TILE_COLORS[k - 1]! }) })}
              onClick={() => onChange({ color: null })}
              style={{ ...swatch, borderColor: look.color === null ? 'var(--ink-700)' : 'transparent', background: 'var(--paper)', color: 'var(--ink-400)' }}>
              <Icon name="x" size={14} />
            </button>
          )}
          {QUICK_TILE_COLORS.map((c, i) => {
            const tc = tileTone(c)!;
            const at = forArticles ? i + 1 : i;
            const count = QUICK_TILE_COLORS.length + (forArticles ? 1 : 0);
            return (
              <button key={c} type="button" role="radio" aria-checked={look.color === c} aria-label={t(TILE_COLOR_NAME[c])} disabled={disabled}
                {...rovingRadio({ index: at, count, checked: look.color === c, anyChecked: forArticles || look.color !== null, onPick: (k) => onChange({ color: forArticles ? (k === 0 ? null : QUICK_TILE_COLORS[k - 1]!) : QUICK_TILE_COLORS[k]! }) })}
                onClick={() => onChange({ color: c })}
                style={{ ...swatch, borderColor: look.color === c ? tc.dot : 'transparent', background: tc.bg }}>
                <span style={{ width: 12, height: 12, borderRadius: 'var(--r-pill)', background: tc.dot }} />
              </button>
            );
          })}
        </div>
      </div>

      {showIcons && (
        <div>
          <span id={`${fieldId}-0`} style={caption}>{t('Pictogramme')}</span>
          <div role="radiogroup" aria-labelledby={`${fieldId}-0`} style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6 }}>
            {icons.map((k, i) => {
              const on = (look.icon ?? QUICK_TILE_DEFAULT_ICON) === k;
              return (
                <button key={k} type="button" role="radio" aria-checked={on} aria-label={k} title={k} disabled={disabled}
                  {...rovingRadio({ index: i, count: icons.length, checked: on, anyChecked: icons.some((x) => x === (look.icon ?? QUICK_TILE_DEFAULT_ICON)), onPick: (n) => onChange({ icon: icons[n]! }) })}
                  onClick={() => onChange({ icon: k })}
                  style={{ height: 34, border: `1px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-xs)', background: on ? 'var(--teal-100)' : 'var(--paper)', color: on ? 'var(--teal-800)' : 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                  <Icon name={k} size={16} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!forArticles && crop && look.photo && (
        <CropDialog name={name} photo={look.photo} tone={tone} aspect={bandAspect(kind, frame, size)}
          onClose={() => setCrop(false)}
          onApply={(framing) => { setCrop(false); onChange({ photo: { ...look.photo!, ...framing } }, t('Cadrage appliqué')); }}
          onRemove={() => { setCrop(false); onChange({ photo: null }, t('Photo retirée — la tuile retombe sur son picto')); }}
          onReplace={() => { setCrop(false); fileRef.current?.click(); }} />
      )}
    </>
  );
}

/**
 * LE DESSIN D'UNE TAILLE DE PHOTO — une tuile miniature : la photo (pleine, en bandeau, en demi-bandeau ou à gauche) et
 * les lignes du texte. Il dit d'un coup d'œil ce que le mot dit.
 */
function SizeGlyph({ variant }: { variant: ReturnType<typeof sizedVariant> }) {
  const photo = 'var(--ink-300)';
  const line = 'var(--ink-200)';
  const frame: CSSProperties = { position: 'relative', width: 26, height: 22, borderRadius: 4, border: '1px solid var(--ink-300)', overflow: 'hidden', background: 'var(--paper)', display: 'block' };
  const bar = (top: number, width: number): CSSProperties => ({ position: 'absolute', left: 3, top, width, height: 2, borderRadius: 1, background: line });
  if (variant === 'photo') return <span aria-hidden style={{ ...frame, background: photo }}><span style={{ ...bar(15, 14), background: 'var(--paper)' }} /></span>;
  if (variant === 'photoLeft' || variant === 'thumb') {
    return <span aria-hidden style={frame}><span style={{ position: 'absolute', left: 2, top: 2, width: 9, height: 9, borderRadius: 2, background: photo }} /><span style={bar(4, 10)} /><span style={{ ...bar(4, 10), left: 13 }} /><span style={bar(16, 12)} /></span>;
  }
  const band = variant === 'vignetteReduced' || variant === 'photoReduced' ? 6 : 11;
  return <span aria-hidden style={frame}><span style={{ position: 'absolute', left: 0, top: 0, right: 0, height: band, background: photo }} /><span style={bar(band + 3, 14)} /><span style={bar(band + 7, 9)} /></span>;
}

/**
 * LA PROPORTION DE CE QUE LA CAISSE MONTRE DE LA PHOTO — le bandeau d'un article ou d'un dossier à son encombrement, sa
 * moitié pour une vignette réduite, ou le caisson carré d'une photo à gauche (`quickBandBox`, charte).
 */
export function bandAspect(kind: QuickTileKind, frame: { w: number; h: number }, size: QuickPhotoSize): number {
  const box = quickBandBox(sizedVariant(kind, size), frame.w, frame.h);
  return box.width / box.height;
}

/**
 * CADRER LA PHOTO — on glisse l'image pour choisir ce qu'on voit, le curseur règle le zoom.
 * ⚠️ RIEN NE CHANGE AVANT « Appliquer » : le cadrage en cours vit ici, et « Annuler » rend la tuile telle qu'elle était.
 */
function CropDialog({ name, photo, tone, aspect, onClose, onApply, onRemove, onReplace }: {
  name: string; photo: StudioTilePhoto; tone: TileTone | null;
  /**
   * La proportion de ce que la caisse montre : le bandeau de la tuile (`bandAspect`). ⚠️ ON CADRE À CETTE PROPORTION,
   * seul rendu qui compte (cadrage A8) : cadré dans une autre forme, le bandeau montrerait en caisse une autre partie
   * de l'image.
   */
  aspect: number; onClose: () => void;
  onApply: (framing: Pick<StudioTilePhoto, 'x' | 'y' | 'zoom'>) => void; onRemove: () => void; onReplace: () => void;
}) {
  const [frame, setFrame] = useState({ x: photo.x, y: photo.y, zoom: photo.zoom });
  const drag = useRef<{ px: number; py: number; x: number; y: number; w: number; h: number } | null>(null);
  const clamp = (v: number, min: number, max: number): number => Math.round(Math.min(max, Math.max(min, v)));
  const down = (e: ReactPointerEvent<HTMLDivElement>): void => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { px: e.clientX, py: e.clientY, x: frame.x, y: frame.y, w: r.width, h: r.height };
  };
  const move = (e: ReactPointerEvent<HTMLDivElement>): void => {
    const d = drag.current;
    if (!d) return;
    // Glisser l'image vers la droite montre ce qui était à sa gauche : le point focal recule. Plus on zoome, plus un
    // même geste déplace peu.
    const k = QUICK_PHOTO_FOCAL_MAX / (frame.zoom / 100);
    setFrame((f) => ({ ...f, x: clamp(d.x - ((e.clientX - d.px) / d.w) * k, 0, QUICK_PHOTO_FOCAL_MAX), y: clamp(d.y - ((e.clientY - d.py) / d.h) * k, 0, QUICK_PHOTO_FOCAL_MAX) }));
  };
  const up = (): void => { drag.current = null; };
  /**
   * Le cadrage au clavier — les flèches déplacent l'IMAGE comme la main qui la glisse (→ la pousse à droite, donc le point
   * de cadrage part à gauche) ; Maj fait un pas quatre fois plus grand.
   */
  const keys = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    const dx = e.key === 'ArrowRight' ? -1 : e.key === 'ArrowLeft' ? 1 : 0;
    const dy = e.key === 'ArrowDown' ? -1 : e.key === 'ArrowUp' ? 1 : 0;
    if (!dx && !dy) return;
    e.preventDefault();
    const step = (QUICK_PHOTO_FOCAL_MAX / 20) * (e.shiftKey ? 4 : 1);
    setFrame((f) => ({ ...f, x: clamp(f.x + dx * step, 0, QUICK_PHOTO_FOCAL_MAX), y: clamp(f.y + dy * step, 0, QUICK_PHOTO_FOCAL_MAX) }));
  };
  return (
    <Modal open onClose={onClose} title={t('Cadrer la photo')} icon="image" width="md"
      subtitle={t('{nom} — glissez l’image pour choisir ce qu’on voit, le curseur pour zoomer.', { nom: name })}
      footer={(
        <>
          <Button onClick={onRemove}>{t('Retirer la photo')}</Button>
          <Button onClick={onReplace}>{t('Remplacer')}</Button>
          <span style={{ flex: 1 }} />
          <Button onClick={onClose}>{t('Annuler')}</Button>
          <Button variant="primary" onClick={() => onApply(frame)}>{t('Appliquer')}</Button>
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center' }}>
        <div onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
          tabIndex={0} onKeyDown={keys} role="group" aria-roledescription={t('zone de cadrage')}
          aria-label={t('Cadrage de la photo — les flèches déplacent l’image, Maj pour aller plus vite')}
          style={{ width: aspect >= 1 ? 300 : Math.round(300 * aspect), height: aspect >= 1 ? Math.round(300 / aspect) : 300, borderRadius: 'var(--r-sm)', overflow: 'hidden', cursor: 'grab', touchAction: 'none', background: tone?.bg ?? 'var(--ink-100)', border: '1px solid var(--ink-200)' }}>
          <span style={{ ...photoStyle(frame, photo.url), pointerEvents: 'none' }} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{t('Zoom')}</span>
          <input type="range" min={QUICK_PHOTO_ZOOM_MIN} max={QUICK_PHOTO_ZOOM_MAX} step={5} value={frame.zoom}
            onChange={(e) => setFrame((f) => ({ ...f, zoom: clamp(Number(e.target.value), QUICK_PHOTO_ZOOM_MIN, QUICK_PHOTO_ZOOM_MAX) }))}
            style={{ flex: 1, accentColor: 'var(--teal-500)' }} />
          <span style={{ fontSize: 'var(--t-dense)', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-700)', minWidth: 40, textAlign: 'right' }}>{`×${(frame.zoom / 100).toFixed(2)}`}</span>
          <button type="button" onClick={() => setFrame({ x: CENTER, y: CENTER, zoom: frame.zoom })} style={smallButton}>{t('Recentrer')}</button>
        </label>
      </div>
    </Modal>
  );
}

const caption: CSSProperties = { display: 'block', fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink-500)', marginBottom: 6 };
const hint: CSSProperties = { display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-400)', marginTop: 5, lineHeight: 1.45 };
const smallButton: CSSProperties = { height: 28, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-800)', fontSize: 'var(--t-meta)', fontWeight: 600 };
const swatch: CSSProperties = { width: 34, height: 34, border: '2px solid transparent', borderRadius: 'var(--r-xs)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 };

/** Les visuels du contrat, pour le banc : la liste de l'écran ne doit pas en oublier ni en inventer. */
export const VISUAL_KEYS: readonly QuickTileVisual[] = VISUALS.map((v) => v.key);
